import React, { useState, useEffect } from 'react';
import { storage } from '../firebase';
import { ref, listAll, getDownloadURL, getMetadata, deleteObject } from 'firebase/storage';

const VideoList = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Función para obtener la lista de videos desde Firebase
  const fetchVideos = async () => {
    const videosRef = ref(storage, 'videos/');
    try {
      const result = await listAll(videosRef);
      const videosData = await Promise.all(
        result.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          const metadata = await getMetadata(itemRef);
          return {
            name: itemRef.name,
            url,
            timeCreated: metadata.timeCreated,
          };
        })
      );
      return videosData;
    } catch (err) {
      console.error('Error al listar videos:', err);
      setError('Error al listar videos: ' + err.message);
      return [];
    }
  };

  // Solicitar permiso para notificaciones al montar el componente
  useEffect(() => {
    if (!("Notification" in window)) {
      console.log("Este navegador no soporta notificaciones");
    } else if (Notification.permission !== "granted" && Notification.permission !== "denied") {
      Notification.requestPermission();
    }
  }, []);

  // Obtener videos y detectar nuevos
  useEffect(() => {
    const loadVideos = async () => {
      const newVideos = await fetchVideos();
      setLoading(false);

      // Comparar con lista previa para detectar nuevos videos
      if (videos.length > 0) {
        const prevVideoNames = videos.map((v) => v.name);
        const newVideoNames = newVideos.map((v) => v.name);
        const addedVideos = newVideos.filter((v) => !prevVideoNames.includes(v.name));

        addedVideos.forEach((video) => {
          if (Notification.permission === "granted") {
            new Notification("Nuevo video subido", {
              body: `${video.name} ha sido añadido a la lista.`,
              icon: "https://cdn-icons-png.flaticon.com/512/148/148767.png", // Ícono opcional
            });
          }
        });
      }
      setVideos(newVideos);
    };

    // Carga inicial
    loadVideos();

    // Actualización periódica cada 10 segundos
    const interval = setInterval(loadVideos, 10000);

    return () => clearInterval(interval);
  }, [videos.length]); // Dependencia en videos.length para evitar bucles innecesarios

  const handleDeleteVideo = async (videoName) => {
    try {
      const videoRef = ref(storage, `videos/${videoName}`);
      await deleteObject(videoRef);
      setVideos((prevVideos) => prevVideos.filter((video) => video.name !== videoName));
    } catch (err) {
      setError('Error al eliminar el video: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Videos Subidos</h1>
        <p style={styles.subtitle}>Cargando videos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Videos Subidos</h1>
        <p style={styles.error}>{error}</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Videos Subidos</h1>
      {videos.length === 0 ? (
        <p style={styles.subtitle}>No hay videos subidos.</p>
      ) : (
        <div style={styles.videosWrapper}>
          {videos.map((video, index) => (
            <div key={index} style={styles.videoCard}>
              <video
                src={video.url}
                style={styles.thumbnail}
                preload="metadata"
                muted
                onClick={(e) => e.target.play()}
              />
              <p style={styles.videoName}>{video.name}</p>
              <p style={styles.videoDate}>
                {new Date(video.timeCreated).toLocaleString()}
              </p>
              <div style={styles.buttonGroup}>
                <a href={video.url} download={video.name} style={styles.downloadLink}>
                  <button style={styles.downloadButton}>Descargar</button>
                </a>
                <button
                  style={styles.deleteButton}
                  onClick={() => handleDeleteVideo(video.name)}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '40px',
    textAlign: 'center',
    color: '#fff',
    minHeight: '100vh',
    fontFamily: "'Montserrat', sans-serif",
    backgroundImage: 'url("https://images.unsplash.com/photo-1489599849927-2ee91ac47135?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 0,
    },
  },
  title: {
    fontSize: '2.5em',
    fontWeight: 'bold',
    marginBottom: '20px',
    position: 'relative',
    zIndex: 1,
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
  },
  subtitle: {
    fontSize: '1.2em',
    color: '#d3d3d3',
    position: 'relative',
    zIndex: 1,
  },
  error: {
    color: '#ff4d4d',
    fontWeight: '500',
    marginTop: '15px',
    position: 'relative',
    zIndex: 1,
  },
  videosWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1,
  },
  videoCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    borderRadius: '8px',
    padding: '15px',
    margin: '15px',
    width: '300px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  thumbnail: {
    width: '100%',
    borderRadius: '8px',
    cursor: 'pointer',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)',
  },
  videoName: {
    fontWeight: '600',
    margin: '10px 0 5px',
    fontSize: '1.1em',
  },
  videoDate: {
    fontSize: '0.9em',
    marginBottom: '15px',
    color: '#d3d3d3',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  downloadLink: {
    textDecoration: 'none',
  },
  downloadButton: {
    cursor: 'pointer',
    backgroundColor: '#e50914',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '1em',
    fontWeight: '600',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: '#f40612',
    },
  },
  deleteButton: {
    cursor: 'pointer',
    backgroundColor: '#555',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '1em',
    fontWeight: '600',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: '#777',
    },
  },
};

export default VideoList;